import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Settings, Clock, Route, LayoutGrid, History, MapPin, ArrowLeft, LogOut, ClipboardList, ChefHat, Bell, AlarmClock, LayoutList, Pizza, Coffee, Wine, CheckCircle2, Crown, ChevronDown, Info } from "lucide-react";
import TicketCard, { SLA, elapsedSec } from "../components/kds/TicketCard";
import { SlaDialog, RoutingDialog } from "../components/kds/StationSettings";

const POLL_MS = 5000;
const COLS = [
  ["received",    "NEW ORDERS",  "#D97706"],
  ["in_progress", "IN PROGRESS", "#D97706"],
  ["ready",       "READY",       "#2E7D32"],
];

// Palette
const C = {
  canvas: "#FAF6EC",
  panel: "#FFFFFF",
  border: "#EFE4D0",
  borderSoft: "#F3EAD6",
  text: "#3B2722",
  muted: "#8B7355",
  softMuted: "#A58F7C",
  burgundy: "#8B1E1E",
  burgundyDark: "#6E1414",
  gold: "#C49A4A",
  green: SLA.green,
  amber: SLA.amber,
  red: SLA.red,
};

const STATION_ICON = { all: LayoutGrid, kitchen: ChefHat, pizza: Pizza, bakery: Coffee, bar: Wine };
const stationIcon = (code) => STATION_ICON[code] || LayoutList;

function Kpi({ label, value, valueColor = C.text, Icon, iconColor = C.gold, iconBg = "#FDF3DC", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg bg-white border px-4 py-3 flex items-center justify-between"
      style={{ borderColor: C.border }}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.muted }}>{label}</div>
        <div className="font-brand font-bold text-3xl leading-tight mt-1" style={{ color: valueColor }}>{value}</div>
      </div>
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: iconBg, color: iconColor }}>
        <Icon size={20} strokeWidth={2} />
      </div>
    </div>
  );
}

function StationTab({ active, code, name, onClick }) {
  const Ico = stationIcon(code);
  return (
    <button
      onClick={onClick}
      data-testid={`kds-station-${code}`}
      className="h-10 px-4 rounded-md text-[12px] font-bold whitespace-nowrap transition-all flex items-center gap-2 border"
      style={{
        background: active ? C.burgundy : "#FFFFFF",
        color: active ? "#FFFFFF" : C.text,
        borderColor: active ? C.burgundy : C.border,
      }}
    >
      <Ico size={14} strokeWidth={2.2} /> {name}
    </button>
  );
}

function SidebarCard({ title, children, testid }) {
  return (
    <div className="rounded-lg bg-white border p-4" style={{ borderColor: C.border }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-3" style={{ color: C.muted }}>{title}</div>
      {children}
    </div>
  );
}

export default function KDS() {
  const { user, can, logout } = useAuth();
  const navigate = useNavigate();
  const [stations, setStations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [station, setStation] = useState(localStorage.getItem("kds_station") || "all");
  const [location, setLocation] = useState(user?.location_id || "all");
  const [tickets, setTickets] = useState([]);
  const [showDone, setShowDone] = useState(true);
  const [busy, setBusy] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [online, setOnline] = useState(true);
  const scoped = !!user?.location_id && !user?.is_admin;

  const load = useCallback(async () => {
    const params = { include_completed: true };
    if (station !== "all") params.station = station;
    if (location !== "all" && !scoped) params.location_id = location;
    try { setTickets(await api.get("/kds/tickets", { params })); setOnline(true); }
    catch { setOnline(false); }
  }, [station, location, scoped]);
  const loadStations = () => api.get("/kds/stations").then(setStations).catch(() => {});
  useEffect(() => { loadStations(); api.get("/locations").then(setLocations).catch(() => {}); }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(iv); clearInterval(c); };
  }, [load]);
  useEffect(() => { localStorage.setItem("kds_station", station); }, [station]);

  const stMeta = useMemo(() => stations.find((s) => s.code === station), [stations, station]);
  const metaFor = (t) => stMeta || stations.find((s) => s.code === t.station_codes?.[0]);
  const stationTabs = useMemo(() => [{ code: "all", name: "All Stations" }, ...stations], [stations]);

  const advance = async (t, next) => {
    setBusy(t.id);
    try {
      const body = { status: next };
      if (station !== "all") body.station = station;
      await api.post(`/kds/tickets/${t.id}/status`, body);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const active = tickets.filter((t) => t.status !== "completed");
  const done = tickets.filter((t) => t.status === "completed");
  const counts = { received: 0, in_progress: 0, ready: 0 };
  active.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
  const overdue = active.filter((t) => elapsedSec(t.created_at) / 60 > (metaFor(t)?.sla_amber ?? 20)).length;
  const totalCompleted = done.length;

  // Today's summary metrics
  const prepDurations = done
    .filter((t) => t.completed_at && t.created_at)
    .map((t) => (new Date(t.completed_at).getTime() - new Date(t.created_at).getTime()) / 1000);
  const avgPrepSec = prepDurations.length ? prepDurations.reduce((a, b) => a + b, 0) / prepDurations.length : 0;
  const fmtDur = (s) => {
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };
  const slaMetPct = (() => {
    const arr = done.filter((t) => t.completed_at && t.created_at);
    if (!arr.length) return null;
    const met = arr.filter((t) => {
      const mins = (new Date(t.completed_at).getTime() - new Date(t.created_at).getTime()) / 60000;
      const meta = metaFor(t);
      return mins <= (meta?.sla_amber ?? 20);
    }).length;
    return Math.round((met / arr.length) * 100);
  })();

  const userInitials = (user?.name || "SM").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const dateStr = clock.toLocaleDateString("en-NG", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const timeStr = clock.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

  const recentDone = [...done].sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0)).slice(0, 4);

  return (
    <div data-testid="kds-page" className="h-screen flex flex-col overflow-hidden" style={{ background: C.canvas }}>
      {/* HEADER */}
      <header className="shrink-0 border-b" style={{ background: C.panel, borderColor: C.border }}>
        <div className="h-[76px] px-6 flex items-center gap-6">
          {/* Brand */}
          <div className="flex items-center gap-3 shrink-0 pr-4 border-r" style={{ borderColor: C.borderSoft }}>
            <img src="/logo.jpg" alt="Sir Monty" className="w-11 h-11 rounded-md object-cover" />
            <div className="leading-tight">
              <div className="font-brand font-bold text-2xl tracking-tight" style={{ color: C.burgundy }}>Sir Monty</div>
              <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.muted }}>KITCHEN DISPLAY SYSTEM</div>
            </div>
          </div>

          {/* Location */}
          <div className="shrink-0">
            <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>LOCATION</div>
            {scoped ? (
              <div className="flex items-center gap-1.5 mt-1 font-semibold text-sm" style={{ color: C.text }} data-testid="kds-location">
                <MapPin size={14} style={{ color: C.gold }} />
                {locations.find((l) => l.id === user.location_id)?.name || "My location"}
              </div>
            ) : (
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="h-8 w-40 border-0 shadow-none px-0 font-semibold text-sm bg-transparent hover:bg-transparent focus:ring-0" data-testid="kds-location">
                  <div className="flex items-center gap-1.5"><MapPin size={14} style={{ color: C.gold }} /><SelectValue /></div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Station */}
          <div className="shrink-0">
            <div className="text-[9px] font-bold tracking-[0.18em]" style={{ color: C.softMuted }}>STATION</div>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger className="h-8 w-44 border-0 shadow-none px-0 font-semibold text-sm bg-transparent hover:bg-transparent focus:ring-0" data-testid="kds-station-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stationTabs.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1" />

          {/* Status */}
          <div className="flex items-center gap-2 h-9 px-3 rounded-full" style={{ background: online ? "#EEF7EE" : "#FDECEC" }} data-testid="kds-online">
            <span className="w-2 h-2 rounded-full" style={{ background: online ? C.green : C.red }} />
            <span className="text-[11px] font-bold tracking-wider" style={{ color: online ? C.green : C.red }}>
              {online ? "ONLINE" : "RECONNECTING"}
            </span>
            <span className="w-px h-4" style={{ background: "#D6E6D6" }} />
            <span className="text-[11px] font-semibold" style={{ color: C.muted }}>Polling · 5s</span>
          </div>

          {/* Clock */}
          <div className="flex items-center gap-2.5 pr-5 border-r shrink-0" style={{ borderColor: C.borderSoft }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#F5EDDC", color: C.gold }}>
              <Clock size={16} />
            </div>
            <div className="leading-tight text-right">
              <div className="font-brand font-bold text-xl tabular-nums" style={{ color: C.text }} data-testid="kds-clock">{timeStr}</div>
              <div className="text-[10px] font-semibold" style={{ color: C.softMuted }}>{dateStr}</div>
            </div>
          </div>

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 shrink-0" data-testid="kds-user-menu">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: C.burgundy, color: "#fff" }}>{userInitials}</div>
                <div className="leading-tight text-left hidden xl:block">
                  <div className="text-sm font-semibold" style={{ color: C.text }}>{user?.name}</div>
                  <div className="text-[10px]" style={{ color: C.softMuted }}>{user?.role?.name}</div>
                </div>
                <ChevronDown size={14} style={{ color: C.softMuted }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel style={{ color: C.muted }}>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {can("dashboard", "view") && (
                <DropdownMenuItem onClick={() => navigate("/")} data-testid="kds-back-erp">
                  <ArrowLeft size={15} className="mr-2" /> Back to ERP
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="kds-logout">
                <LogOut size={15} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* KPI + STATION TABS ROW */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-start justify-end mb-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="kds-settings-btn" className="h-9 px-3 rounded-md border bg-white flex items-center gap-2 text-[12px] font-semibold" style={{ borderColor: C.border, color: C.text }}>
                <Settings size={14} strokeWidth={2.2} /> Settings
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="kds-settings-menu">
              <DropdownMenuLabel style={{ color: C.muted }}>Kitchen display</DropdownMenuLabel>
              {can("kds", "edit") && <DropdownMenuItem onClick={() => setDlg("sla")} data-testid="kds-setting-sla"><Clock size={15} className="mr-2" /> SLA Thresholds</DropdownMenuItem>}
              {can("kds", "edit") && <DropdownMenuItem onClick={() => setDlg("route")} data-testid="kds-setting-route"><Route size={15} className="mr-2" /> Station Routing</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setShowDone((v) => !v)} data-testid="kds-setting-done">
                <History size={15} className="mr-2" /> {showDone ? "Hide recently completed" : "Show recently completed"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-stretch gap-3 flex-wrap 2xl:flex-nowrap">
          <div className="grid grid-cols-4 gap-3 flex-1 min-w-[560px]">
            <Kpi testid="kpi-active"     label="Active Orders" value={active.length}      Icon={ClipboardList} iconColor={C.amber} iconBg="#FEF3D8" />
            <Kpi testid="kpi-inprogress" label="In Progress"   value={counts.in_progress} Icon={ChefHat}       iconColor={C.burgundy} iconBg="#FBE4E4" />
            <Kpi testid="kpi-ready"      label="Ready"         value={counts.ready}       Icon={Bell}          iconColor={C.green}   iconBg="#E4F1E4" />
            <Kpi testid="kpi-overdue"    label="Overdue"       value={overdue}            Icon={AlarmClock}    iconColor={C.red}     iconBg="#FBE4E4" valueColor={overdue > 0 ? C.red : C.text} />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto shrink-0" data-testid="kds-station-chips">
            {stationTabs.map((s) => (
              <StationTab key={s.code} active={station === s.code} code={s.code} name={s.name} onClick={() => setStation(s.code)} />
            ))}
          </div>
        </div>
      </div>

      {/* MAIN BOARD + SIDEBAR */}
      <main className="flex-1 min-h-0 px-6 pb-3 grid gap-4" style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 300px" }} data-testid="kds-board">
        {COLS.map(([st, label, color]) => {
          const list = active.filter((t) => t.status === st);
          return (
            <section key={st} data-testid={`kds-col-${st}`} className="flex flex-col min-h-0">
              <div className="flex items-center gap-3 pb-2">
                <span className="text-[11px] font-bold tracking-[0.18em]" style={{ color: C.muted }}>{label}</span>
                <span className="text-[11px] font-bold tabular-nums" style={{ color: C.softMuted }}>{list.length}</span>
                <div className="flex-1 h-[3px] rounded-full" style={{ background: color, opacity: 0.85 }} />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
                {list.length === 0 && (
                  <div className="rounded-lg border border-dashed py-10 text-center text-sm flex flex-col items-center gap-2 bg-white/60"
                       style={{ borderColor: C.border, color: C.softMuted }}>
                    <ChefHat size={22} strokeWidth={1.5} />
                    No orders here yet
                    {station !== "all" && (
                      <button onClick={() => setStation("all")}
                        data-testid={`kds-col-${st}-view-all`}
                        className="text-[11px] font-semibold underline" style={{ color: C.burgundy }}>
                        Viewing "{stMeta?.name || station}" only — show all stations
                      </button>
                    )}
                  </div>
                )}
                {list.map((t) => (
                  <TicketCard
                    key={t.id}
                    ticket={t}
                    station={station !== "all" ? station : null}
                    stationMeta={metaFor(t)}
                    busy={busy === t.id}
                    onAdvance={can("kds", "edit") ? advance : () => toast.error("No permission to update tickets")}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* RIGHT SIDEBAR */}
        <aside className="flex flex-col gap-3 min-h-0 overflow-y-auto pb-1" data-testid="kds-sidebar">
          <SidebarCard title="SLA Guide" testid="kds-sla-guide">
            <div className="space-y-2 text-[12px]">
              {[
                { c: C.green, range: "0 – 10 min", label: "On Time" },
                { c: C.amber, range: "11 – 20 min", label: "Warning" },
                { c: C.red,   range: "21+ min",    label: "Overdue" },
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: r.c }} />
                    <span className="font-semibold" style={{ color: C.text }}>{r.range}</span>
                  </div>
                  <span className="font-semibold" style={{ color: r.c }}>{r.label}</span>
                </div>
              ))}
            </div>
          </SidebarCard>

          <SidebarCard title="Today's Summary" testid="kds-summary">
            <div className="space-y-2.5 text-[13px]">
              <div className="flex items-center justify-between">
                <span style={{ color: C.muted }}>Completed</span>
                <span className="font-brand font-bold" style={{ color: C.text }}>{totalCompleted}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: C.muted }}>Avg. Prep Time</span>
                <span className="font-brand font-bold tabular-nums" style={{ color: C.gold }}>{avgPrepSec ? fmtDur(avgPrepSec) : "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: C.muted }}>SLA Met</span>
                <span className="font-brand font-bold" style={{ color: C.green }}>{slaMetPct == null ? "—" : `${slaMetPct}%`}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ color: C.muted }}>Overdue</span>
                <span className="font-brand font-bold" style={{ color: overdue > 0 ? C.red : C.text }}>{overdue}</span>
              </div>
            </div>
          </SidebarCard>

          {showDone && (
            <SidebarCard title="Recently Completed" testid="kds-recent-completed">
              {recentDone.length === 0 && <div className="text-[12px]" style={{ color: C.softMuted }}>No completed orders yet.</div>}
              <div className="space-y-2.5">
                {recentDone.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-[12px]">
                    <div className="min-w-0">
                      <div className="font-bold" style={{ color: C.text }}>#{t.order_number}</div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: C.softMuted }}>
                        {(t.order_type || "").replace("_", " ")}
                        {t.table ? ` • Table ${t.table}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="tabular-nums" style={{ color: C.softMuted }}>
                        {t.completed_at ? new Date(t.completed_at).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", hour12: false }) : ""}
                      </span>
                      <CheckCircle2 size={14} style={{ color: C.green }} />
                    </div>
                  </div>
                ))}
              </div>
            </SidebarCard>
          )}
        </aside>
      </main>

      {/* FOOTER */}
      <footer className="shrink-0 h-10 border-t px-6 flex items-center justify-between text-[11px]" style={{ background: C.panel, borderColor: C.border, color: C.softMuted }}>
        <div className="flex items-center gap-1.5">
          <Info size={12} /> Auto refresh every 5 seconds
        </div>
        <div className="flex items-center gap-2 font-brand italic" style={{ color: C.text }}>
          <Crown size={13} style={{ color: C.gold }} /> Great Food, Great Moments.
        </div>
        <div className="font-semibold">Sir Monty KDS v1.0.0</div>
      </footer>

      <SlaDialog open={dlg === "sla"} onClose={() => setDlg(null)} stations={stations} onSaved={loadStations} />
      <RoutingDialog open={dlg === "route"} onClose={() => { setDlg(null); loadStations(); }} stations={stations} />
    </div>
  );
}
